import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CampaignIcon from "@mui/icons-material/Campaign";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HandymanIcon from "@mui/icons-material/Handyman";
import GroupsIcon from "@mui/icons-material/Groups";
import { formatName } from "../../../helpers/helpers";
import { filterChatUsers } from "./chatSearchUtils";
import {
  MAX_DIRECT_MESSAGE_MEMBERS,
  getChannelDisplayName,
  getDirectMessageOtherMembers,
  getRoleLabel,
  isAutomatedChannel,
  isGroupDirectMessage,
  isSupportChannel,
  isTicketChannel,
} from "./chatSidebarSectionUtils";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// Same input treatment as the rest of the app (rounded, violet focus ring)
const FIELD_SX = {
  backgroundColor: "white",
  borderRadius: "10px",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  // focused wins over hover (declared last, and explicit for the hover case)
  "&.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focused:hover .MuiOutlinedInput-notchedOutline":
    { borderColor: "#6d28d9", borderWidth: "1px" },
};

const CHANNEL_EVENTS = [
  "channel.updated",
  "member.added",
  "member.removed",
  "member.updated",
];

// Which of those change WHO is in the channel, or what role they hold there,
// and so make a paged roster snapshot wrong rather than merely stale-looking.
const MEMBERSHIP_EVENTS = ["member.added", "member.removed", "member.updated"];

// These change a user's own fields (display name, online state). The roster
// set is unaffected, so a snapshot is patched in place: refetching every page
// each time a teammate goes online would be a paging storm on exactly the
// channels big enough to need paging.
const USER_EVENTS = ["user.presence.changed", "user.updated"];

// Mutes live on the current user, not on the channel, so this one is
// client-level too: the sheet reads channel.muteStatus(), which reads
// client.mutedChannels, and muting from the conversation header or another
// tab left the Mute/Unmute label showing the old state.
const CLIENT_EVENTS = ["notification.channel_mutes_updated"];

// Stream routes an event to channel listeners only when it carries a cid, and
// the user.* events carry none: they are client-level, dispatched once per
// user however many channels that user is in. channel.on("user.updated", ...)
// therefore never fires, so these have to be taken off the client.
const clientOf = (channel) => {
  try {
    return channel?.getClient?.() || null;
  } catch {
    // getClient throws once the client has disconnected
    return null;
  }
};

// Stream max page size for queryMembers
const MEMBER_PAGE_SIZE = 300;

/**
 * Stream only hydrates the first 100 members into channel state, so anything
 * bigger has to be fetched and paged explicitly or the roster silently stops
 * at 100 (and member search only ever sees those first 100). Returns null when
 * channel state already holds everyone, so small channels keep their live
 * member updates.
 */
const useFullMemberList = (channel, open, memberCount, membershipRevision) => {
  const [members, setMembers] = useState(null);
  useEffect(() => {
    const hydrated = Object.keys(channel?.state?.members || {}).length;
    if (!channel || !open || (memberCount ?? hydrated) <= hydrated) {
      setMembers(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const collected = [];
      let offset = 0;
      try {
        for (;;) {
          const response = await channel.queryMembers(
            {},
            [{ user_id: 1 }],
            { limit: MEMBER_PAGE_SIZE, offset }
          );
          const page = response?.members || [];
          collected.push(...page);
          if (page.length < MEMBER_PAGE_SIZE) break;
          offset += MEMBER_PAGE_SIZE;
        }
        if (!cancelled) setMembers(collected);
      } catch {
        // Fall back to whatever channel state has rather than an empty sheet
        if (!cancelled) setMembers(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // membershipRevision: a promotion, a join, or a removal lands as an
    // event, not as a change to any of the values above, so without it the
    // sheet keeps showing the roster it fetched when it opened.
  }, [channel, open, memberCount, membershipRevision]);

  // Keep the snapshot's user objects current without going back to the wire.
  useEffect(() => {
    if (!channel || !open) return undefined;
    const patchUser = (event) => {
      const user = event?.user;
      if (!user?.id) return;
      setMembers((previous) => {
        if (!previous) return previous;
        let changed = false;
        const next = previous.map((member) => {
          if ((member.user_id || member.user?.id) !== user.id) return member;
          changed = true;
          return { ...member, user: { ...member.user, ...user } };
        });
        return changed ? next : previous;
      });
    };
    const client = clientOf(channel);
    if (!client) return undefined;
    const subscriptions = USER_EVENTS.map((eventName) =>
      client.on(eventName, patchUser)
    );
    return () => {
      subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    };
  }, [channel, open]);
  return members;
};

// Re-render when the channel's data/members change so the sheet stays live.
// Returns the membership revision, which the paged roster depends on: the rest
// of the events only need the component to render again.
const useChannelRevision = (channel, open) => {
  const [, setRevision] = useState(0);
  const [membershipRevision, setMembershipRevision] = useState(0);
  useEffect(() => {
    if (!channel || !open) return undefined;
    const subscriptions = CHANNEL_EVENTS.map((eventName) =>
      channel.on(eventName, () => {
        setRevision((value) => value + 1);
        if (MEMBERSHIP_EVENTS.includes(eventName)) {
          setMembershipRevision((value) => value + 1);
        }
      })
    );
    // A client-level user event fires for everyone this client watches, so it
    // only counts as a change here if the person is in this channel. The paged
    // case is covered by the snapshot patcher instead, which re-renders by
    // replacing the row it changed.
    const client = clientOf(channel);
    if (client) {
      // Stream refreshes its mute cache from reconnect snapshots before
      // notifying listeners. Regular heartbeat events carry no user snapshot.
      subscriptions.push(client.on("health.check", (event) => {
        if (event.me) setRevision((value) => value + 1);
      }));
      subscriptions.push(
        ...CLIENT_EVENTS.map((eventName) =>
          client.on(eventName, () => setRevision((value) => value + 1))
        )
      );
      subscriptions.push(
        ...USER_EVENTS.map((eventName) =>
          client.on(eventName, (event) => {
            const userId = event?.user?.id;
            if (userId && channel.state?.members?.[userId]) {
              setRevision((value) => value + 1);
            }
          })
        )
      );
    }
    return () => {
      subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    };
  }, [channel, open]);
  return membershipRevision;
};

const ActionPill = ({ icon, label, onClick, tone = "default", disabled, loading = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-busy={loading || undefined}
    className={`flex min-w-[72px] flex-col items-center gap-y-1 rounded-xl px-3 py-2.5 text-xs font-medium transition-colors ${
      tone === "danger"
        ? "bg-rose-50 text-rose-700 hover:bg-rose-100"
        : "bg-slate-50 text-slate-700 hover:bg-slate-100"
    } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
  >
    <span
      className={tone === "danger" ? "text-rose-700" : "text-violet-700"}
      style={{ display: "inline-flex" }}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : icon}
    </span>
    {label}
  </button>
);

const InlineConfirm = ({ message, confirmLabel, onConfirm, onCancel, busy }) => (
  <div className="mt-2 flex items-center justify-between gap-x-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">
    <span className="min-w-0">{message}</span>
    <div className="flex shrink-0 items-center gap-x-2">
      <button
        type="button"
        className="rounded-md px-2 py-1 text-slate-600 hover:bg-white"
        onClick={onCancel}
        disabled={busy}
      >
        Cancel
      </button>
      <button
        type="button"
        className="rounded-md bg-rose-600 px-2.5 py-1 font-medium text-white hover:bg-rose-700"
        onClick={onConfirm}
        disabled={busy}
      >
        {busy ? "…" : confirmLabel}
      </button>
    </div>
  </div>
);

const Initial = ({ name, size = 40, className = "bg-violet-700" }) => (
  <div
    className={`flex shrink-0 items-center justify-center rounded-full text-white ${className}`}
    style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
  >
    {name?.trim()?.[0]?.toUpperCase() || "?"}
  </div>
);

const HeaderAvatar = ({ channel, otherMembers, GroupAvatar }) => {
  const size = 56;
  if (channel.type === "messaging") {
    if (isGroupDirectMessage(channel)) {
      return <GroupAvatar members={otherMembers} size={size} />;
    }
    return <Initial name={otherMembers[0]?.user?.name} size={size} />;
  }
  const iconStyle = { fontSize: "28px" };
  if (isTicketChannel(channel)) {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
        <HandymanIcon style={iconStyle} />
      </div>
    );
  }
  if (isSupportChannel(channel)) {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-violet-700 text-2xl font-semibold text-white">
        ?
      </div>
    );
  }
  if (channel.type === "announcement") {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gray-700 text-white">
        <CampaignIcon style={iconStyle} />
      </div>
    );
  }
  if (channel.type === "daily") {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
        <CalendarTodayIcon style={iconStyle} />
      </div>
    );
  }
  if (isAutomatedChannel(channel)) {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
        <GroupsIcon style={iconStyle} />
      </div>
    );
  }
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-orange-600 text-2xl text-white">
      #
    </div>
  );
};

/**
 * One details sheet for every conversation kind: custom channels (manage
 * name/description/members/moderators), automated channels (read-only
 * roster), group DMs (add people, leave) and 1:1 DMs (person card).
 * Rights are derived from the channel itself rather than passed in.
 */
function ConversationDetailsModal({
  open,
  onClose,
  channel,
  currentUserId,
  todayDateStr,
  storeNames,
  chatUsers = [],
  GroupAvatar,
  onEditChannel,
  onAddChannelMembers,
  onAddPeopleToDirectMessage,
  onRemoveMember,
  onPromoteModerator,
  onDemoteModerator,
  onMute,
  onUnmute,
  onTogglePin,
  onLeave,
  onDeleteChannel,
}) {
  const membershipRevision = useChannelRevision(channel, open);
  const memberCount = channel?.data?.member_count;
  const fullMembers = useFullMemberList(channel, open, memberCount, membershipRevision);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [addingPeople, setAddingPeople] = useState(false);
  const [pickedPeople, setPickedPeople] = useState([]);
  const [busyAction, setBusyAction] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null); // {kind, userId?}
  const [menuState, setMenuState] = useState({ anchor: null, member: null });

  useEffect(() => {
    if (!open) {
      setEditing(false);
      setAddingPeople(false);
      setPickedPeople([]);
      setMemberQuery("");
      setConfirming(null);
      setError(null);
      setBusyAction(null);
    }
  }, [open, channel?.cid]);

  if (!channel) return null;

  const members = fullMembers || Object.values(channel.state?.members || {});

  const isDirectMessage = channel.type === "messaging";
  const isGroup = isGroupDirectMessage(channel);
  const isOneOnOne = isDirectMessage && !isGroup;
  const automated = isAutomatedChannel(channel);
  const otherMembers = getDirectMessageOtherMembers(channel, currentUserId);
  // Own membership comes from channel state, not from the roster: on a channel
  // big enough to page, the roster is the first 100 by user id until the fetch
  // lands, and stays that way if it fails, so a moderator sitting outside that
  // first page would lose their management controls here.
  const ownMembership = channel.state?.membership;
  const me =
    ownMembership?.user?.id === currentUserId
      ? ownMembership
      : members.find((member) => member.user?.id === currentUserId);
  const isOwner = channel.data?.created_by?.id === currentUserId;
  const isModerator = me?.channel_role === "channel_moderator";
  const canManageChannel = !isDirectMessage && !automated && (isOwner || isModerator);
  const canAddPeople = canManageChannel || isDirectMessage;
  const canLeave = !automated && (isGroup || !isDirectMessage);
  const otherManagersExist = members.some(
    (member) =>
      member.user?.id !== currentUserId &&
      (member.user?.id === channel.data?.created_by?.id ||
        member.channel_role === "channel_moderator")
  );
  // The last managing member (owner or moderator) can't leave, or nobody
  // could run the channel afterwards
  const leaveBlockedForOwner =
    canLeave &&
    !isDirectMessage &&
    (isOwner || isModerator) &&
    !otherManagersExist;
  // The creator can delete; a moderator can once the creator has left
  // (Stream's created_by is immutable, so this keeps channels deletable)
  // This scans the roster, which is only the first page while the full list is
  // in flight and stays that way if the fetch failed. Deleting a channel is
  // destructive, so an incomplete roster is read as "the creator is still
  // here", hiding Delete rather than offering it on a partial list.
  const rosterIsComplete = members.length >= (memberCount ?? members.length);
  const ownerIsMember =
    !rosterIsComplete ||
    members.some((member) => member.user?.id === channel.data?.created_by?.id);
  const canDelete =
    !isDirectMessage && !automated && (isOwner || (isModerator && !ownerIsMember));
  const isMuted = Boolean(channel.muteStatus?.()?.muted);
  const isPinned = Boolean(channel.state?.membership?.pinned_at);
  const title = getChannelDisplayName(channel, currentUserId, todayDateStr, storeNames);
  const description = channel.data?.description || "";
  const onlineCount = members.filter((member) => member.user?.online).length;
  // member_count is authoritative; the loaded roster can still be in flight
  const rosterCount = memberCount ?? members.length;
  const otherUser = otherMembers[0]?.user;
  const otherChatUser = chatUsers.find(
    (user) => user.id?.toString() === otherUser?.id?.toString()
  );

  const memberIds = new Set(members.map((member) => member.user?.id?.toString()));
  const addablePeople = chatUsers.filter(
    (user) =>
      !memberIds.has(user.id?.toString()) &&
      (isDirectMessage || user.can_join_store_channel !== false)
  );
  const groupCapacity = isDirectMessage
    ? Math.max(0, MAX_DIRECT_MESSAGE_MEMBERS - members.length)
    : Infinity;

  const sortedMembers = [...members].sort((a, b) => {
    const rank = (member) => {
      if (member.user?.id === channel.data?.created_by?.id && !isDirectMessage) return 0;
      if (member.channel_role === "channel_moderator" && !isDirectMessage) return 1;
      return 2;
    };
    return (
      rank(a) - rank(b) ||
      Number(Boolean(b.user?.online)) - Number(Boolean(a.user?.online)) ||
      (a.user?.name || "").localeCompare(b.user?.name || "")
    );
  });
  const visibleMembers = memberQuery.trim()
    ? sortedMembers.filter((member) =>
        (member.user?.name || "").toLowerCase().includes(memberQuery.trim().toLowerCase())
      )
    : sortedMembers;

  const run = async (key, action) => {
    setBusyAction(key);
    setError(null);
    try {
      await action();
    } catch (actionError) {
      console.error("Conversation details action failed:", actionError);
      setError(
        actionError?.response?.data?.system?.message ||
          "That didn't go through. Please try again."
      );
    } finally {
      setBusyAction(null);
    }
  };

  const startEditing = () => {
    setDraftName(channel.data?.name || "");
    setDraftDescription(description);
    setEditing(true);
  };

  const saveEdits = () =>
    run("edit", async () => {
      const nextName = draftName.trim();
      if (!nextName) return;
      await onEditChannel(channel, nextName, draftDescription.trim());
      setEditing(false);
    });

  const submitAddPeople = () =>
    run("add", async () => {
      if (!pickedPeople.length) return;
      if (isDirectMessage) {
        await onAddPeopleToDirectMessage(channel, pickedPeople);
      } else {
        await onAddChannelMembers(channel, pickedPeople);
      }
      setPickedPeople([]);
      setAddingPeople(false);
    });

  const subtitle = () => {
    if (isOneOnOne) {
      const role = getRoleLabel(otherChatUser?.permission_type);
      return (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
          {role && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
              {role}
            </span>
          )}
          {otherChatUser?.email && <span className="truncate">{otherChatUser.email}</span>}
          <span className="flex items-center gap-x-1">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                otherUser?.online ? "bg-emerald-500" : "bg-slate-300"
              }`}
            />
            {otherUser?.online ? "Online" : "Offline"}
          </span>
        </div>
      );
    }
    if (isGroup) {
      return (
        <p className="mt-1 text-sm text-slate-500">
          Group message · {members.length} people
        </p>
      );
    }
    return (
      <p className="mt-1 text-sm text-slate-500">
        {rosterCount} {rosterCount === 1 ? "member" : "members"} · {onlineCount} online
        {automated && " · Membership is managed automatically"}
      </p>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <div className="absolute right-3 top-3 z-10">
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Header */}
          <div className="flex items-start gap-x-4 px-6 pb-4 pt-6">
            <HeaderAvatar
              channel={channel}
              otherMembers={otherMembers}
              GroupAvatar={GroupAvatar}
            />
            <div className="min-w-0 flex-1 pr-8">
              {editing ? (
                <div className="flex flex-col gap-y-3 pt-1">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">
                      Channel name
                    </label>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") saveEdits();
                        if (event.key === "Escape") setEditing(false);
                      }}
                      InputProps={{ sx: FIELD_SX }}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">
                      Description (optional)
                    </label>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="What is this channel about?"
                      value={draftDescription}
                      onChange={(event) => setDraftDescription(event.target.value)}
                      InputProps={{ sx: FIELD_SX }}
                    />
                  </div>
                  <div className="flex items-center gap-x-2">
                    <button
                      type="button"
                      className="rounded-lg bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800 disabled:opacity-50"
                      onClick={saveEdits}
                      disabled={!draftName.trim() || busyAction === "edit"}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-x-2">
                    <h2 className="min-w-0 truncate text-xl font-semibold text-slate-900">
                      {title}
                    </h2>
                    {automated && (
                      <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xxs font-medium text-emerald-700">
                        Automated
                      </span>
                    )}
                    {canManageChannel && (
                      <IconButton
                        size="small"
                        onClick={startEditing}
                        aria-label="Edit name and description"
                        sx={{ color: "#6d28d9" }}
                      >
                        <EditIcon style={{ fontSize: "16px" }} />
                      </IconButton>
                    )}
                  </div>
                  {subtitle()}
                  {description && !isDirectMessage && (
                    <p className="mt-2 whitespace-pre-line text-sm text-slate-700">
                      {description}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 px-6 pb-5">
            <ActionPill
              icon={isMuted ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
              label={busyAction === "mute" ? "Saving…" : isMuted ? "Unmute" : "Mute"}
              onClick={() =>
                run("mute", async () => (isMuted ? onUnmute(channel) : onMute(channel)))
              }
              disabled={busyAction === "mute"}
              loading={busyAction === "mute"}
            />
            <ActionPill
              icon={isPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
              label={isPinned ? "Unpin" : "Pin"}
              onClick={() => run("pin", async () => onTogglePin(channel))}
              disabled={busyAction === "pin"}
            />
            {canAddPeople && (
              <ActionPill
                icon={<PersonAddAlt1Icon fontSize="small" />}
                label="Add people"
                onClick={() => {
                  setAddingPeople((value) => !value);
                  setConfirming(null);
                }}
                disabled={isDirectMessage && groupCapacity === 0}
              />
            )}
            {canLeave && (
              <ActionPill
                icon={<LogoutIcon fontSize="small" />}
                label="Leave"
                tone="danger"
                disabled={leaveBlockedForOwner}
                onClick={() => setConfirming({ kind: "leave" })}
              />
            )}
            {canDelete && (
              <ActionPill
                icon={<DeleteOutlineIcon fontSize="small" />}
                label="Delete"
                tone="danger"
                onClick={() => setConfirming({ kind: "delete" })}
              />
            )}
          </div>

          {confirming?.kind === "leave" && (
            <div className="px-6 pb-4">
              <InlineConfirm
                message={
                  isGroup
                    ? "Leave this group message? You'll stop receiving its messages."
                    : "Leave this channel? A moderator can add you back later."
                }
                confirmLabel="Leave"
                busy={busyAction === "leave"}
                onCancel={() => setConfirming(null)}
                onConfirm={() => run("leave", async () => onLeave(channel))}
              />
            </div>
          )}
          {confirming?.kind === "delete" && (
            <div className="px-6 pb-4">
              <InlineConfirm
                message="Delete this channel for everyone? Messages can't be recovered."
                confirmLabel="Delete channel"
                busy={busyAction === "delete"}
                onCancel={() => setConfirming(null)}
                onConfirm={() => run("delete", async () => onDeleteChannel(channel))}
              />
            </div>
          )}

          {leaveBlockedForOwner && (
            <p className="px-6 pb-3 text-xs text-slate-500">
              You're the only moderator here. Make someone else a moderator before
              leaving{canDelete ? ", or delete the channel" : ""}.
            </p>
          )}

          {isDirectMessage && groupCapacity === 0 && (
            <p className="px-6 pb-3 text-xs text-slate-500">
              Group messages hold up to {MAX_DIRECT_MESSAGE_MEMBERS} people. For a bigger
              group, create a channel.
            </p>
          )}

          {error && (
            <div className="mx-6 mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}

          {/* Add people */}
          {addingPeople && canAddPeople && (
            <div className="mx-6 mb-4 rounded-xl border border-violet-200 bg-violet-50/40 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Add people
              </p>
              <Autocomplete
                multiple
                fullWidth
                autoHighlight
                openOnFocus
                size="small"
                options={addablePeople}
                value={pickedPeople}
                onChange={(event, value) =>
                  setPickedPeople(
                    isDirectMessage ? value.slice(0, groupCapacity) : value
                  )
                }
                getOptionLabel={(option) => formatName(option.name)}
                isOptionEqualToValue={(option, value) =>
                  option.id?.toString() === value.id?.toString()
                }
                filterOptions={(options, state) => filterChatUsers(options, state.inputValue)}
                getOptionDisabled={(option) =>
                  isDirectMessage &&
                  pickedPeople.length >= groupCapacity &&
                  !pickedPeople.some((p) => p.id?.toString() === option.id?.toString())
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      size="small"
                      label={formatName(option.name)}
                      sx={{ bgcolor: "#ede9fe", color: "#5b21b6" }}
                    />
                  ))
                }
                renderOption={(optionProps, option) => {
                  const role = getRoleLabel(option.permission_type);
                  return (
                    <li {...optionProps} key={option.id}>
                      <div className="flex w-full min-w-0 items-center gap-x-3">
                        <Initial name={option.name} size={28} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-x-2">
                            <span className="truncate">{formatName(option.name)}</span>
                            {role && (
                              <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xxs text-slate-600">
                                {role}
                              </span>
                            )}
                          </div>
                          {option.email && (
                            <div className="truncate text-xs text-slate-500">{option.email}</div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder="Type a name or email"
                    sx={{ "& .MuiOutlinedInput-root": FIELD_SX }}
                  />
                )}
              />
              {isOneOnOne && (
                <p className="mt-2 text-xs text-slate-500">
                  Adding people starts a new group message; this conversation stays private.
                </p>
              )}
              <div className="mt-3 flex items-center gap-x-2">
                <button
                  type="button"
                  className="rounded-lg bg-violet-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-800 disabled:opacity-50"
                  onClick={submitAddPeople}
                  disabled={!pickedPeople.length || busyAction === "add"}
                >
                  {busyAction === "add" ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : isOneOnOne ? (
                    "Start group"
                  ) : (
                    "Add"
                  )}
                </button>
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
                  onClick={() => {
                    setAddingPeople(false);
                    setPickedPeople([]);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Members */}
          {!isOneOnOne && (
            <div className="px-6 pb-6">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {isGroup ? "People" : "Members"} · {rosterCount}
                </p>
              </div>
              {members.length > 8 && (
                <div className="mb-2 flex items-center gap-x-2 rounded-lg bg-slate-50 px-3 py-2">
                  <SearchIcon style={{ fontSize: "18px" }} className="text-slate-400" />
                  <input
                    className="w-full bg-transparent text-sm outline-none"
                    placeholder="Search members"
                    value={memberQuery}
                    onChange={(event) => setMemberQuery(event.target.value)}
                  />
                </div>
              )}
              <div className="divide-y divide-slate-100">
                {visibleMembers.map((member) => {
                  const user = member.user || {};
                  const memberIsOwner =
                    !isDirectMessage && user.id === channel.data?.created_by?.id;
                  const memberIsModerator =
                    !isDirectMessage && member.channel_role === "channel_moderator";
                  const isMe = user.id === currentUserId;
                  const canActOn = canManageChannel && !isMe && !memberIsOwner;
                  const rowConfirm =
                    confirming?.kind === "remove" && confirming.userId === user.id;
                  return (
                    <div key={user.id} className="py-2">
                      <div className="flex items-center gap-x-3">
                        <div className="relative">
                          <Initial name={user.name} size={32} />
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
                              user.online ? "bg-emerald-500" : "bg-slate-300"
                            }`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-x-2">
                            <span className="truncate text-sm text-slate-900">
                              {formatName(user.name)}
                              {isMe && <span className="text-slate-400"> (you)</span>}
                            </span>
                            {memberIsOwner && (
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xxs text-slate-600">
                                Owner
                              </span>
                            )}
                            {memberIsModerator && (
                              <span className="rounded bg-violet-100 px-1.5 py-0.5 text-xxs text-violet-700">
                                Moderator
                              </span>
                            )}
                          </div>
                        </div>
                        {canActOn && (
                          <IconButton
                            size="small"
                            aria-label={`Actions for ${user.name}`}
                            onClick={(event) =>
                              setMenuState({ anchor: event.currentTarget, member })
                            }
                          >
                            <MoreHorizIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                      {rowConfirm && (
                        <InlineConfirm
                          message={`Remove ${formatName(user.name)} from this ${
                            isGroup ? "group" : "channel"
                          }?`}
                          confirmLabel="Remove"
                          busy={busyAction === `remove-${user.id}`}
                          onCancel={() => setConfirming(null)}
                          onConfirm={() =>
                            run(`remove-${user.id}`, async () => {
                              await onRemoveMember(channel, user.id);
                              setConfirming(null);
                            })
                          }
                        />
                      )}
                    </div>
                  );
                })}
                {!visibleMembers.length && (
                  <p className="py-4 text-center text-sm text-slate-500">No members match.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <Menu
          anchorEl={menuState.anchor}
          open={Boolean(menuState.anchor)}
          onClose={() => setMenuState({ anchor: null, member: null })}
        >
          {menuState.member?.channel_role === "channel_moderator" ? (
            <MenuItem
              onClick={() => {
                const target = menuState.member;
                setMenuState({ anchor: null, member: null });
                run(`demote-${target.user?.id}`, async () =>
                  onDemoteModerator(channel, target.user?.id)
                );
              }}
            >
              Remove as moderator
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                const target = menuState.member;
                setMenuState({ anchor: null, member: null });
                run(`promote-${target.user?.id}`, async () =>
                  onPromoteModerator(channel, target.user?.id)
                );
              }}
            >
              Make moderator
            </MenuItem>
          )}
          <MenuItem
            sx={{ color: "#be123c" }}
            onClick={() => {
              const target = menuState.member;
              setMenuState({ anchor: null, member: null });
              setConfirming({ kind: "remove", userId: target.user?.id });
            }}
          >
            Remove from {isGroup ? "group" : "channel"}
          </MenuItem>
        </Menu>
      </Box>
    </Modal>
  );
}

export default ConversationDetailsModal;
